import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { fetchVehicle } from "../../../api/vehicle";
import { mapErrors } from "../../../components/Utils";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { VehicleInfo } from "../../../components/dashboard/vehicles/vehicle-info";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { PMTabs } from "../../../components/utils/PMTabs";
import { DocumentCenter } from "../../../components/dashboard/documents/document-center";
import { DrivingTripsTable } from "../../../components/dashboard/driving_record/trips/trips-table";
import { getDrivingTripsAsync } from "../../../slices/driving/trip";
import { ChecklistTypeSelector } from "../../../components/dashboard/quality/checklists/checklistTypeSelector";
import VehicleServicePanel from "../../../components/dashboard/vehicles/service/vehicle-service-panel";
import VehicleDamagePanel from "../../../components/dashboard/vehicles/damage/vehicle-damage-panel";
import VehicleInsurancePanel from "../../../components/dashboard/vehicles/insurance/vehicle-insurance-panel";
import { VehicleCardsPanel } from "../../../components/dashboard/vehicles/cards/vehicle-cards-panel";
import { fetchChecklists } from "../../../api/quality/checklist";
import {
  ArrowPathRoundedSquareIcon,
  PencilIcon,
  InformationCircleIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Vehicle Trips Component
const VehicleTrips = ({ vehicleId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const tripsState = useSelector((store) => store.drivingTrips);
  const { isLoading, data, totalCount } = tripsState;

  const [controller, setController] = useState({
    filters: [{ name: "vehicle_id", value: vehicleId }],
    page: 1,
    per_page: 20,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
  });

  useEffect(() => {
    if (vehicleId) {
      const payload = {
        ...controller,
        filters: [{ name: "vehicle_id", value: vehicleId }],
      };
      dispatch(getDrivingTripsAsync(payload));
    }
  }, [dispatch, vehicleId, controller.page, controller.per_page]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t("Vehicle Trips")}
          </h3>
          <p className="text-sm text-gray-500">
            {totalCount} {t("trips found")}
          </p>
        </div>
      </div>

      {data && data.length > 0 ? (
        <DrivingTripsTable
          itemState={tripsState}
          page={controller.page}
          onPageChange={handlePageChange}
          perPage={controller.per_page}
          onItemsSelect={() => {}}
        />
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {t("No trips found for this vehicle")}
          </p>
        </div>
      )}
    </div>
  );
};

// Vehicle Internal Controls Component
const VehicleInternalControls = ({ vehicleId, vehicle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastChecklist, setLastChecklist] = useState(null);
  const [allChecklists, setAllChecklists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showChecklistTypeSelector, setShowChecklistTypeSelector] =
    useState(false);

  useEffect(() => {
    async function loadChecklists() {
      if (!vehicleId) return;
      setIsLoading(true);
      try {
        const response = await fetchChecklists({
          filters: [{ name: "vehicle_id", value: vehicleId }],
          per_page: 100, // Get all checklists for this vehicle
          page: 1,
          sortBy: "created_at",
          sort: "DESC",
        });
        const list = response?.data || [];
        setAllChecklists(list);
        setLastChecklist(list.length > 0 ? list[0] : null);
      } catch (e) {
        setLastChecklist(null);
        setAllChecklists([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadChecklists();
  }, [vehicleId]);

  const handleCreateChecklist = () => {
    setShowChecklistTypeSelector(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-gray-100 text-gray-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "finished":
        return "bg-green-100 text-green-800";
      case "approved":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "open":
        return t("Open");
      case "in_progress":
        return t("In Progress");
      case "finished":
        return t("Finished");
      case "approved":
        return t("Approved");
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t("Internal Controls")}
          </h3>
          <p className="text-sm text-gray-500">
            {t("Vehicle safety and maintenance checklists")}
          </p>
        </div>
        <button
          onClick={handleCreateChecklist}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          <ClipboardDocumentCheckIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
          {t("Create Checklist")}
        </button>
      </div>

      {/* Last Checklist Information */}
      {lastChecklist && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {t("Last Internal Control")}
            </h4>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Checklist Number")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {lastChecklist.number}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Title")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {lastChecklist.title}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {lastChecklist.date}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Status")}
                </dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      lastChecklist.status
                    )}`}
                  >
                    {getStatusText(lastChecklist.status)}
                  </span>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Responsible")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {lastChecklist.responsible_executive?.displayTitle}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("Items Approved")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {lastChecklist.items.filter((item) => item.isApproved).length}{" "}
                  / {lastChecklist.items.length}
                </dd>
              </div>
            </div>

            {/* Checklist Items Summary */}
            <div className="mt-6">
              <h5 className="text-sm font-medium text-gray-900 mb-3">
                {t("Checklist Items")}
              </h5>
              <div className="space-y-2">
                {lastChecklist.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-900">{item.title}</span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.isApproved
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {item.isApproved ? t("Approved") : t("Not Approved")}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* All Checklists List */}
            {allChecklists.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h5 className="text-sm font-medium text-gray-900 mb-3">
                  {t("All Checklists")} ({allChecklists.length})
                </h5>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {allChecklists.map((checklist) => (
                    <button
                      key={checklist.id}
                      onClick={() =>
                        navigate(`/dashboard/hse-ks/checklists/${checklist.id}`)
                      }
                      className="w-full text-left flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {checklist.number || checklist.title}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              checklist.status
                            )}`}
                          >
                            {getStatusText(checklist.status)}
                          </span>
                        </div>
                        {checklist.title && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {checklist.title}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {checklist.date}
                        </p>
                      </div>
                      <svg
                        className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No checklist found */}
      {!lastChecklist && (
        <div className="text-center py-8">
          <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("No checklists found")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("Get started by creating a new checklist")}
          </p>
        </div>
      )}

      {/* Checklist Type Selector Dialog */}
      <ChecklistTypeSelector
        open={showChecklistTypeSelector}
        onClose={() => setShowChecklistTypeSelector(false)}
        vehicleId={vehicleId}
        vehicle={vehicle}
      />
    </div>
  );
};

export const Vehicle = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    vehicle: {},
  });

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, vehicle } = itemState;

  const item = vehicle;

  useEffect(() => {
    getVehicle();
  }, [id]);

  const getVehicle = async () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    return fetchVehicle(id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            vehicle: res.data,
            error: false,
            errors: [],
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          vehicle: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    {
      name: "Detaljer",
      icon: InformationCircleIcon,
      component: <VehicleInfo item={item} onRefresh={getVehicle} />,
      active: true,
    },
    {
      name: "Trips",
      icon: TruckIcon,
      component: <VehicleTrips vehicleId={id} />,
      active: true,
    },
    {
      name: "Service",
      icon: WrenchScrewdriverIcon,
      component: <VehicleServicePanel vehicleId={id} />,
      active: true,
    },
    {
      name: "Damage Reports",
      icon: ExclamationTriangleIcon,
      component: <VehicleDamagePanel vehicleId={id} canManage={true} />,
      active: true,
    },
    {
      name: "Documents",
      icon: DocumentTextIcon,
      component: (
        <DocumentCenter
          module="vehicle"
          moduleParam={id}
          title={t("Vehicle Documents")}
          subtitle={t("Manage vehicle documents and files")}
          canUpload={true}
          allowedFileTypes={["pdf", "doc", "docx", "jpg", "jpeg", "png", "gif"]}
        />
      ),
      active: true,
    },
    {
      name: "Insurance",
      icon: InformationCircleIcon,
      component: <VehicleInsurancePanel vehicleId={id} />,
      active: true,
    },
    {
      name: "Internal Controls",
      icon: ClipboardDocumentCheckIcon,
      component: <VehicleInternalControls vehicleId={id} vehicle={item} />,
      active: true,
    },
    {
      name: "Cards & Services",
      icon: CreditCardIcon,
      component: <VehicleCardsPanel vehicleId={id} canManage={true} />,
      active: true,
    },
  ];

  const headerActions = [
    {
      component: null,
      label: "Sync with Automile",
      description: "",
      path: null,
      action: () => console.log("Sync with Automile"),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: (
        <HistoriesModalButton module={"vehicle"} module_param={item?.id} />
      ),
      label: "History",
      description: "",
      path: null,
      action: () => console.log("History"),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceEmpty />;
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={vehicle?.number_plate + " - " + vehicle?.model}
          meta={[{ label: "Vehicle", icon: ArrowPathRoundedSquareIcon }]}
          mainButton={{
            label: "Edit",
            action: () => navigate(`/dashboard/vehicles/${id}/edit`),
            path: null,
            color: "",
            icon: PencilIcon,
          }}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
              <PMTabs
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                tabs={tabs}
              />
              <div className="mt-6">{tabs[selectedTab].component}</div>
            </div>
          </div>
        </DashboardHeader>
      </>
    );
  };

  return <>{renderContent()}</>;
};
